import { ticketApi, type TicketCounts } from '@/api/tickets'
import { useIsAdmin } from '@/components/auth/AdminGuard'
import { isTicketUnread } from '@/lib/ticket-read-state'
import { useAuthStore } from '@/store/auth'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

const POLL_INTERVAL = 30_000 // 30 seconds

type SnapshotMap = Map<string, { updatedAt: string; subject: string; lastSenderType?: string }>

/**
 * Which sender type represents "incoming" activity for the current viewer?
 * - Admin sees activity from 'user'
 * - User sees activity from 'admin'
 */
function incomingSenderType(isAdmin: boolean): string {
  return isAdmin ? 'user' : 'admin'
}

/**
 * Module-level ref to track the last snapshot used for toast dedup.
 * Ensures toasts fire exactly once even when multiple components
 * call useTicketCounts().
 */
let lastToastedDataRef: object | null = null
let prevSnapshotsGlobal: SnapshotMap | null = null

/**
 * Hook that polls for ticket activity and shows toast notifications
 * ONLY when the OTHER party sends a message.
 *
 * - Admin: notified when lastSenderType === 'user'
 * - User: notified when lastSenderType === 'admin'
 */
export function useTicketCounts(): TicketCounts | undefined {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isAdmin = useIsAdmin()

  const { data } = useQuery<TicketCounts>({
    queryKey: isAdmin ? ['admin', 'ticket-counts'] : ['my', 'ticket-counts'],
    queryFn: isAdmin ? ticketApi.adminCounts : ticketApi.myCounts,
    enabled: isAuthenticated,
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL / 2,
  })

  // Detect changes by comparing ticket updatedAt snapshots.
  // Uses module-level refs so toasts fire exactly once per data change,
  // regardless of how many components mount this hook.
  useEffect(() => {
    if (!data?.snapshots) return
    // Skip if we already processed this exact data object
    if (data === lastToastedDataRef) return
    lastToastedDataRef = data

    const incoming = incomingSenderType(isAdmin)

    // Build current snapshot map
    const currentMap: SnapshotMap = new Map(
      data.snapshots.map((s) => [
        s.id,
        { updatedAt: s.updatedAt, subject: s.subject, lastSenderType: s.lastSenderType },
      ]),
    )

    const prevMap = prevSnapshotsGlobal

    // Only notify after the first successful fetch (skip initial load)
    if (prevMap !== null) {
      const changedTickets: string[] = []

      for (const [id, cur] of currentMap) {
        // Only notify about messages from the OTHER party
        if (cur.lastSenderType !== incoming) continue

        const prev = prevMap.get(id)
        if (!prev) {
          // Brand-new ticket from the other party
          changedTickets.push(cur.subject)
        } else if (prev.updatedAt !== cur.updatedAt) {
          // Existing ticket got a new message from the other party
          changedTickets.push(cur.subject)
        }
      }

      if (changedTickets.length > 0) {
        const count = changedTickets.length
        const first = changedTickets[0] ?? ''
        const firstSubject = first.length > 40 ? first.slice(0, 40) + '…' : first

        if (isAdmin) {
          toast.info(
            count === 1
              ? 'New activity in ticket'
              : `New activity in ${count} tickets`,
            {
              description: firstSubject,
              action: {
                label: 'View',
                onClick: () => {
                  window.location.href = '/admin/tickets'
                },
              },
              duration: 8000,
            },
          )
        } else {
          toast.info(
            count === 1
              ? 'New reply on your ticket'
              : `Updates in ${count} of your tickets`,
            {
              description: firstSubject,
              action: {
                label: 'View',
                onClick: () => {
                  window.location.href = '/support'
                },
              },
              duration: 8000,
            },
          )
        }
      }
    }

    prevSnapshotsGlobal = currentMap
  }, [data, isAdmin])

  return data
}

/**
 * Returns the badge count: number of truly unread tickets where the last
 * message was sent by the OTHER party AND the user hasn't read it yet.
 * Reacts instantly when a ticket detail page is opened (markTicketRead).
 * Returns 0 when loading or unauthenticated.
 */
export function useOpenTicketCount(): number {
  const data = useTicketCounts()
  const isAdmin = useIsAdmin()
  const [, bump] = useState(0)

  // Re-render immediately when any ticket is marked as read
  useEffect(() => {
    const handler = () => bump((n) => n + 1)
    window.addEventListener('ghostflow-ticket-read', handler)
    return () => window.removeEventListener('ghostflow-ticket-read', handler)
  }, [])

  if (!data?.snapshots) return 0

  const incoming = incomingSenderType(isAdmin)
  return data.snapshots.filter(
    (s) => isTicketUnread(s.id, s.updatedAt, s.lastSenderType, incoming),
  ).length
}
